import { useDisclosure } from "@mantine/hooks";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useGetApi } from "src/hooks";
import { useDispatch } from "src/redux/hooks";
import { resetState, setLogbook } from "src/redux/slices/generatorSlice";
import { convertToUUIDKeys } from "src/redux/utils/convertToUUIDKeys";
import { Notification } from "src/utils/Notification";

import { type IPreviewResponse } from "./LogbookPreview.types";

export const useLogbookPreview = () => {
  const { logbookId } = useParams();
  const dispatch = useDispatch();
  const [parts, setParts] = useState<IPreviewResponse[]>([]);
  const [selectedPartName, setSelectedPartName] = useState("");
  const [opened, { open, close }] = useDisclosure();

  const mapPartLogbook = useCallback((data: any) => {
    if (data?.logbook?.lbdata) {
      const { sections, ...lbdata } = data.logbook.lbdata;
      return {
        lbname: data?.logbook?.lbname,
        sections: convertToUUIDKeys(sections).convertedSections,
        ...lbdata,
      };
    }
    return { lbname: data?.logbook?.lbname, sections: [] };
  }, []);

  const mapGetPreviewLogbook = useCallback(
    (data: any) => {
      if (data?.logbook?.lbdata?.type === "collection") {
        return { lbname: data.logbook.lbname, ...data.logbook.lbdata };
      } else {
        dispatch(
          setLogbook(
            convertToUUIDKeys(data.logbook.lbdata.sections).convertedSections,
          ),
        );
        return { lbname: data.logbook.lbname, type: "logbook" };
      }
    },
    [dispatch],
  );

  const { refetch: fetchLogbook } = useGetApi<IPreviewResponse>({
    request: "get-preview-logbook",
    fetchOnInit: false,
    mapper: mapPartLogbook,
  });

  const { data } = useGetApi<IPreviewResponse>({
    request: "get-preview-logbook",
    body: {
      params: {
        lbid: logbookId,
      },
    },
    mapper: mapGetPreviewLogbook,
  });

  const handleParts = useCallback(async () => {
    if (data?.parts) {
      try {
        const promises = await Promise.all(
          data.parts.map(
            async lbid => await fetchLogbook({ params: { lbid } }),
          ),
        );
        setParts(promises);
      } catch {
        Notification(
          "Error",
          "Something went wrong during fetching part logbooks",
          "red",
        );
      }
    }
  }, [data?.parts, fetchLogbook]);

  useEffect(() => {
    void handleParts();
  }, [handleParts]);

  useEffect(() => {
    return () => {
      dispatch(resetState());
    };
  }, [dispatch]);

  const customLinks = useCallback(
    (prev: Array<{ title: string; href: string }>) => {
      prev[0].href = `${prev[0].href}?lbtype=available`;
      prev[1].title = `${data?.lbname ?? ""} Preview`;
      return prev;
    },
    [data?.lbname],
  );

  const handleCardClick = useCallback(
    (part: IPreviewResponse) => {
      dispatch(setLogbook(part.sections));
      setSelectedPartName(part.lbname);
      open();
    },
    [dispatch, open],
  );

  const handleCloseModal = useCallback(() => {
    dispatch(setLogbook([]));
    setSelectedPartName("");
    close();
  }, [close, dispatch]);

  return {
    data,
    parts,
    customLinks,
    opened,
    onCardClick: handleCardClick,
    onClose: handleCloseModal,
    selectedPartName,
  };
};
